import './app.less';
import { useThrottleFn } from 'ahooks';
import React, { useEffect, useRef } from 'react';
import { PluginType, type IExportPlugin } from '@/version-types';
import { EdgelessHost } from './components';
import { useFramework, useViewSize } from './hooks';

const useGetPlugins = (plugins: IExportPlugin[]) => {
    return plugins
        .sort((a, b) => a.sort - b.sort)
        .map((plugin) => <React.Fragment key={plugin.name}>{plugin.render?.()}</React.Fragment>);
};

const useListenerPluginsData = (type: PluginType) => {
    const framework = useFramework();
    const { registration } = framework.service;
    return registration.getPlugins()[type];
};

const SystemPlugins = React.memo(() => {
    return <>{useGetPlugins(useListenerPluginsData(PluginType.SYSTEM))}</>;
});

const ViewportPlugins = React.memo(() => {
    return <>{useGetPlugins(useListenerPluginsData(PluginType.VIEWPORT))}</>;
});

const StagePlugins = React.memo(() => {
    return <>{useGetPlugins(useListenerPluginsData(PluginType.STAGE))}</>;
});

export const App = () => {
    const framework = useFramework();
    const { editor } = framework.service;
    // 独立弹窗会在 mount 前设置尺寸，不能只监听挂载后的尺寸变更。
    const viewSize = useViewSize();

    const nodeRef = useRef<HTMLDivElement>(null);
    const { run: mouseMoveHandle } = useThrottleFn(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            editor.__emitMouseMoveEvent(ev);
        },
        { wait: 5 },
    );

    useEffect(() => {
        const mouseWheelHandle = (ev: WheelEvent) => editor.__emitWheelEvent(ev);
        nodeRef.current?.addEventListener('wheel', mouseWheelHandle);
        return () => {
            nodeRef.current?.removeEventListener('wheel', mouseWheelHandle);
        };
    }, []);

    return (
        <>
            <div
                ref={nodeRef}
                data-editor-id={framework.identity}
                className="editor-host"
                style={{ width: viewSize.width, height: viewSize.height }}
                onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.__emitContextMenuEvent(e);
                }}
                onMouseMove={mouseMoveHandle}
                onMouseDown={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    editor.__emitMouseDownEvent(ev);
                }}
                onMouseUp={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    editor.__emitMouseUpEvent(ev);
                }}
                onDrop={(ev: React.DragEvent<HTMLDivElement>) => {
                    editor.__emitDropLayerEvent(ev);
                }}
            >
                <EditorHost />
            </div>
        </>
    );
};

const EditorHost: React.FC = React.memo(() => {
    const viewSize = useViewSize();

    if (!viewSize.width || !viewSize.height) return null;

    return (
        <>
            <SystemPlugins />
            <EdgelessHost stagePlugins={<StagePlugins />} />
            <ViewportPlugins />
        </>
    );
});
